// 登录审计面板（v6.22）——超级管理员后台查看员工登录记录
// 数据源：Vault XEAOS/Audit/LoginLog-{date}.md（服务端 /audit-log 记录，含 IP/归属地/设备）
// 功能：①登录记录列表（时间/用户/操作/IP·归属地/设备/结果）②按用户登录次数统计 ③登录失败记录
// 仅超级管理员可见（Settings 中条件渲染）
import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, MapPin, User, MonitorSmartphone, RefreshCw, AlertTriangle } from "lucide-react";

interface LoginRow {
  time: string;
  date: string;
  username: string;
  action: string;
  success: boolean;
  role: string;
  ip: string;
  location: string;
  device: string;
}

const DAYS = 14; // 近 14 天

const parseRows = (date: string, content: string): LoginRow[] => {
  const rows: LoginRow[] = [];
  for (const line of content.split("\n")) {
    if (!line.trim().startsWith("|")) continue;
    const cols = line.split("|").map((c) => c.trim());
    // 表头/分隔行跳过
    if (cols.length < 7) continue;
    const [t0, time, username, action, result, role, ip, device] = cols;
    if (t0 === "时间" || time === "---" || !time) continue;
    const success = result.includes("成功");
    // IP 列可能含"IP（归属地）"
    let ipRaw = ip || "";
    let location = "";
    const locMatch = ipRaw.match(/^([\d.]+|[A-Fa-f0-9:]+)（([^）]*)）$/);
    if (locMatch) {
      ipRaw = locMatch[1];
      location = locMatch[2];
    }
    rows.push({ time, date, username, action, success, role, ip: ipRaw, location, device: device || "" });
  }
  return rows;
};

export default function LoginAuditPanel() {
  const [rows, setRows] = useState<LoginRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const all: LoginRow[] = [];
    // 读近 14 天文件
    const dates: string[] = [];
    for (let i = 0; i < DAYS; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const p = (n: number) => String(n).padStart(2, "0");
      dates.push(`${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`);
    }
    await Promise.all(
      dates.map(async (date) => {
        try {
          const resp = await fetch(
            `/vault-api/read?path=${encodeURIComponent(`XEAOS/Audit/LoginLog-${date}.md`)}`
          ).catch(() => null);
          if (resp?.ok) {
            const r = await resp.json();
            if (r.success && r.content) {
              all.push(...parseRows(date, String(r.content)));
            }
          }
        } catch {
          // 忽略单日失败
        }
      })
    );
    all.sort((a, b) => `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`));
    setRows(all);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // 按用户统计登录次数
  const userStats = new Map<string, { total: number; success: number; failed: number; lastIp: string; lastLocation: string }>();
  for (const r of rows) {
    const s = userStats.get(r.username) || { total: 0, success: 0, failed: 0, lastIp: "", lastLocation: "" };
    s.total++;
    if (r.success) s.success++;
    else s.failed++;
    if (r.success) {
      s.lastIp = r.ip || s.lastIp;
      s.lastLocation = r.location || s.lastLocation;
    }
    userStats.set(r.username, s);
  }
  const stats = Array.from(userStats.entries()).sort((a, b) => b[1].total - a[1].total);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck size={14} className="text-emerald-400" />
          <span className="text-[12px] font-semibold text-white">登录审计（近 {DAYS} 天）</span>
          <span className="text-[9px] font-mono text-gray-500">服务端记录 · 含 IP/归属地/设备</span>
        </div>
        <button
          onClick={() => void load()}
          className="px-2 py-1 rounded text-[10px] font-mono text-gray-400 border border-white/[0.08] hover:bg-white/[0.04]"
        >
          刷新
        </button>
      </div>

      {loading ? (
        <div className="text-[11px] text-gray-500 py-6 text-center">加载中…</div>
      ) : (
        <>
          {/* 按用户统计 */}
          <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <User size={12} className="text-sky-400" />
              <span className="text-[11px] font-semibold text-sky-300">按用户登录次数</span>
            </div>
            {stats.length === 0 ? (
              <div className="text-[10px] text-gray-500">暂无登录记录</div>
            ) : (
              <div className="space-y-1.5">
                {stats.map(([name, s]) => (
                  <div key={name} className="flex items-center gap-2 text-[10px]">
                    <span className="text-gray-200 w-24 truncate">{name}</span>
                    <span className="text-emerald-400">成功 {s.success}</span>
                    <span className={s.failed > 0 ? "text-red-400" : "text-gray-600"}>失败 {s.failed}</span>
                    <span className="text-gray-500">共 {s.total} 次</span>
                    {s.lastIp && (
                      <span className="text-gray-400 flex items-center gap-0.5 ml-auto">
                        <MapPin size={9} /> {s.lastIp} {s.lastLocation ? `（${s.lastLocation}）` : ""}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {stats.some(([, s]) => s.failed > 0) && (
              <div className="mt-2 flex items-center gap-1.5 text-[9px] text-amber-300/80">
                <AlertTriangle size={10} /> 存在登录失败记录——可能是密码输错或异常尝试，建议关注
              </div>
            )}
          </div>

          {/* 登录记录明细 */}
          <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <MonitorSmartphone size={12} className="text-purple-400" />
              <span className="text-[11px] font-semibold text-purple-300">登录记录明细</span>
              <span className="text-[9px] font-mono text-gray-600 ml-auto">{rows.length} 条</span>
            </div>
            {rows.length === 0 ? (
              <div className="text-[10px] text-gray-500">暂无登录记录（员工登录后会出现在这里）</div>
            ) : (
              <div className="max-h-[360px] overflow-y-auto pr-1">
                <table className="w-full text-[9px] font-mono">
                  <thead className="sticky top-0 bg-[#11131a]">
                    <tr className="text-gray-500 text-left">
                      <th className="py-1 pr-2">时间</th>
                      <th className="pr-2">用户</th>
                      <th className="pr-2">操作</th>
                      <th className="pr-2">结果</th>
                      <th className="pr-2">IP（归属地）</th>
                      <th>设备</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-t border-white/[0.04] text-gray-300">
                        <td className="py-1 pr-2 whitespace-nowrap text-gray-500">
                          {r.date.slice(5)} {r.time}
                        </td>
                        <td className="pr-2">{r.username}</td>
                        <td className="pr-2">{r.action}</td>
                        <td className={`pr-2 ${r.success ? "text-emerald-400" : "text-red-400"}`}>
                          {r.success ? "成功" : "失败"}
                        </td>
                        <td className="pr-2 whitespace-nowrap">
                          {r.ip || "-"}
                          {r.location ? `（${r.location}）` : ""}
                        </td>
                        <td className="max-w-[140px] truncate text-gray-500" title={r.device}>
                          {r.device || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
